import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout, Tabs } from '@oneaudi/unified-web-components';
import { vehicleContext } from '../VehicleProvider';
import OverviewContent from '../OverviewContent';
import NonAudiOverview from '../NonAudiOverview';
import StandardEquipmentContent from '../StandardEquipmentContent';
import OptionalEquipmentContent from '../OptionalEquipmentContent';
import DealersNoteContent from '../DealerNoteContent';
import type { VehicleDetails } from '../../types';
import { IS_T3_WEBSITE } from '../../constants/environment';
import { ContentWrapper, TabsContainer } from './Tabs.styles';

const TabsComponent: React.FC = () => {
  const { vehicle } = useContext(vehicleContext);
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('VDPFSS1');
  const [modalOpen, setModalOpen] = useState(false);

  const { dealerNote, staticDealerInfo, equipments } = vehicle as VehicleDetails;
  const { isDealerNoteVisible } = staticDealerInfo;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const optionalEquipments = equipments?.optionalEquipments || [];
  const nonAudi = vehicle?.vehicleType === 'non-audi';

  if (!vehicle) return null;

  const tabItems = [];

  tabItems.push({
    id: 'VDPFSS1',
    label: t('featuresAndSpecsSection.categories.overview.title'),
    content: (
      <div data-testid="overview-tab-content">
        {nonAudi ? (
          <NonAudiOverview />
        ) : (
          <OverviewContent modalOpen={modalOpen} setModalOpen={setModalOpen} />
        )}
      </div>
    ),
  });

  if (IS_T3_WEBSITE && dealerNote) {
    tabItems.push({
      id: 'VDPFSS5',
      label: t('featuresAndSpecsSection.categories.dealerNotes.title'),
      content: (
        <div data-testid="dealer-notes-tab-content">
          <DealersNoteContent />
        </div>
      ),
    });
  }

  if (!nonAudi) {
    tabItems.push({
      id: 'VDPFSS2',
      label: t('featuresAndSpecsSection.categories.standardEquipment.title'),
      content: (
        <ContentWrapper data-testid="standard-equipment-tab-content">
          <StandardEquipmentContent />
        </ContentWrapper>
      ),
    });
  }

  if (!nonAudi && optionalEquipments.length > 0) {
    tabItems.push({
      id: 'VDPFSS3',
      label: t('featuresAndSpecsSection.categories.optionalEquipment.title'),
      content: (
        <div data-testid="optional-equipment-tab-content">
          <OptionalEquipmentContent />
        </div>
      ),
    });
  }

  if (dealerNote && isDealerNoteVisible) {
    tabItems.push({
      id: 'VDPFSS4',
      label: t('featuresAndSpecsSection.categories.vehicleDescription'),
      content: (
        <div data-testid="vehicle-description-tab-content">
          <DealersNoteContent />
        </div>
      ),
    });
  }

  return (
    <Layout>
      <TabsContainer>
        <Tabs
          id="vehicle-details-tabs"
          size="md"
          className="vehicleTab"
          items={tabItems.map((tab) => ({
            label: tab.label,
            content: tab.content,
            active: activeTab === tab.id,
            onClick: () => {
              setActiveTab(tab.id);
            },
          }))}
          scrollButtons={{
            left: { ariaLabel: 'Scroll Back' },
            right: { ariaLabel: 'Scroll Forward' },
          }}
        />
      </TabsContainer>
    </Layout>
  );
};

export default TabsComponent;
